import axios from 'axios';
import { getApiUrl } from '@/config/api';
import authService from '@/shared/services/auth';

class CatCuentaContableService {
  constructor() {
    this.cuentas = [];
    this.loading = false;
    this.error = null;
    this.selectedEmpresa = null;
  }

  async initialize() {
    // Esperar un poco para que la empresa se seleccione
    await this.waitForEmpresa();
    await this.listar();
  }

  // Esperar a que la empresa esté seleccionada
  async waitForEmpresa() {
    let attempts = 0;
    const maxAttempts = 50; // 5 segundos máximo
    
    while (attempts < maxAttempts) {
      this.selectedEmpresa = authService.getSelectedEmpresa();
      if (this.selectedEmpresa && this.selectedEmpresa.EmpresaId) {
        console.log('Empresa encontrada para CatCuentaContable:', this.selectedEmpresa);
        return;
      }
      
      // Esperar 100ms antes del siguiente intento
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    // Si no se encuentra empresa después de los intentos
    this.error = 'No se pudo obtener la empresa seleccionada';
    console.error('No se pudo obtener empresa para CatCuentaContable después de', maxAttempts, 'intentos');
  }

  async listar() {
    this.loading = true;
    this.error = null;
    
    try {
      // Verificar que tenemos empresa seleccionada
      if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
        this.selectedEmpresa = authService.getSelectedEmpresa();
        if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
          this.loading = false;
          return { success: false, data: [], message: 'Debe elegir una empresa antes de poder continuar.' };
        }
      }

      const token = authService.getToken();
      const response = await axios.post(getApiUrl('/api/CatCuentaContable/listar'), {
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      
      // Filtrar por empresa en el frontend como medida de seguridad adicional
      const allCuentas = response.data || [];
      this.cuentas = allCuentas.filter(cuenta => cuenta.EmpresaId === this.selectedEmpresa.EmpresaId);
      this.loading = false;
      
      console.log('Cuentas cargados:', this.cuentas.length, 'de', allCuentas.length, 'total');
      console.log('Empresa del usuario:', this.selectedEmpresa.EmpresaId);
      this.loading = false;
      
      console.log('Cuentas contables cargadas:', this.cuentas.length);
      
      return {
        success: true,
        data: this.cuentas,
        message: 'Cuentas contables cargadas exitosamente'
      };
    } catch (error) {
      console.error('Error al cargar cuentas contables:', error);
      this.error = error.message;
      this.cuentas = [];
      this.loading = false;
      throw error;
    }
  }

  async guardar(cuentaData) {
    try {
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const dataToSend = {
        ...cuentaData,
        EmpresaId: selectedEmpresa?.EmpresaId || cuentaData.EmpresaId
      };

      const response = await axios.post(getApiUrl('/api/CatCuentaContable/guardar'), dataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return {
        success: true,
        data: response.data,
        message: 'Cuenta contable guardada exitosamente'
      };
    } catch (error) {
      console.error('Error al guardar cuenta contable:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al guardar cuenta contable'
      };
    }
  }

  async borrar(cuentaData) {
    try {
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const dataToSend = {
        ...cuentaData,
        EmpresaId: selectedEmpresa?.EmpresaId || cuentaData.EmpresaId
      };

      const response = await axios.post(getApiUrl('/api/CatCuentaContable/borrar'), dataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return {
        success: true,
        data: response.data,
        message: 'Cuenta contable eliminada exitosamente'
      };
    } catch (error) {
      console.error('Error al eliminar cuenta contable:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al eliminar cuenta contable'
      };
    }
  }

  async lectura(clave) {
    try {
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const response = await axios.post(getApiUrl('/api/CatCuentaContable/lectura'), {
        Clave: clave,
        EmpresaId: selectedEmpresa?.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error al leer cuenta contable:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al leer cuenta contable'
      };
    }
  }

  async recargar() {
    await this.listar();
  }

  async downloadBulk() {
    try {
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const response = await axios.post(getApiUrl('/api/CatCuentaContable/descarga-masiva'), {
        EmpresaId: selectedEmpresa?.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });

      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cuentas_contables_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        message: 'Descarga iniciada correctamente'
      };
    } catch (error) {
      console.error('Error al descargar cuentas contables:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al descargar cuentas contables'
      };
    }
  }
}

export default CatCuentaContableService;
